/** @jsx jsx */
import React, { Dispatch, Fragment, SetStateAction } from 'react';
import { jsx } from '@emotion/core';
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { Link, navigate } from 'gatsby';
import Avatar from '@material-ui/core/Avatar';
import colors from '../../../../../themes/main-colors';
import { Search } from '../../controls/Search';
import Menu, { MenuItem } from '../../../../menu/Menu';
import compact from 'lodash/compact';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import Tooltip from '@material-ui/core/Tooltip';
import Pagination from '../../../../pagination';
import { PaginationProps } from '../../../../pagination/Pagination';
import { PageSize } from '../../../../pagination/types';
import PGEButton from '../../../../buttons';

type DesktopViewProps = {
  searchValue: string;
  setSearchValue: Dispatch<SetStateAction<string>>;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
  setIsVisibleAdd: Dispatch<SetStateAction<boolean>>;
  setSelectedData: Dispatch<SetStateAction<boolean>>;
  isVisible: any;
  isVisibleAdd: any;
  selectedData: any;
  setUserId: any;
  data: any;
  records: any;
  maxAllowedUsers: any;
  onPageChange: any;
  input: any;
};

export const Installer2DesktopView = (props: DesktopViewProps) => {
  const {
    searchValue,
    setSearchValue,
    setIsVisible,
    setIsVisibleAdd,
    isVisible,
    isVisibleAdd,
    setUserId,
    data,
    records,
    maxAllowedUsers,
    onPageChange,
    input,
    setSelectedData,
  } = props;
  const theme = useTheme();
  const { content } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_INSTALLER_MANAGE_USER_SCREEN,
  );

  const onEdit = (value: any, event: any) => {
    setSelectedData(value);
    setIsVisibleAdd(!isVisibleAdd);
  };

  const onDelete = (value: any, event: any) => {
    setIsVisible(!isVisible);
    setUserId(value?.id);
    setSelectedData(value);
  };

  const AdminUserMenu: Array<MenuItem<any>> = compact([
    { text: 'Edit', onClick: onEdit },
  ]);

  const EditorUserMenu: Array<MenuItem<any>> = compact([
    { text: 'Edit', onClick: onEdit },
    { text: 'Delete user', onClick: onDelete },
  ]);

  const paginationProps: PaginationProps = {
    count: data?.length,
    pageSize: input?.pagination?.perPage,
    pageSizes: [],
    page: input?.pagination?.page,
    visibleOnSinglePage: true,
    onPageChange: (newPage: number, newPageSize: PageSize) => {
      onPageChange(newPage, newPageSize);
    },
  };

  return (
    <Fragment>
      <Fragment>
        <Grid
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: theme.spacing(2),
          }}
        >
          <Grid
            css={{
              width: '85%',
              marginLeft: theme.spacing(2),
              marginRight: theme.spacing(2),
            }}
          >
            <Search
              content={content}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
            />
          </Grid>
          <Grid
            css={{
              width: '15%',
              marginLeft: theme.spacing(2),
              marginRight: theme.spacing(2),
            }}
          >
            <Tooltip
              title={
                data?.length >= maxAllowedUsers ? (
                  <h3 style={{ fontSize: '14px', lineHeight: '14px' }}>
                    {content.get('MAX_AMOUNT_USERS_PERMITTED') || ''}
                  </h3>
                ) : (
                  ''
                )
              }
              placement="top"
            >
              <div>
                <PGEButton
                  css={{
                    '& .MuiButton-label': {
                      gap: '5px',
                    },
                  }}
                  style={{
                    cursor: 'pointer',
                    borderRadius: '30px',
                    width: '100%',
                    height: '100%',
                    padding: '8px',
                  }}
                  role="button"
                  onClick={() => {
                    setIsVisibleAdd(!isVisibleAdd);
                  }}
                  disabled={data?.length >= maxAllowedUsers}
                >
                  <AddCircleOutlineIcon />
                  {content.get('ADD_USER')}
                </PGEButton>
              </div>
            </Tooltip>
          </Grid>
        </Grid>
      </Fragment>
      <Fragment>
        <TableContainer component={Box}>
          <Table
            size="medium"
            css={{
              borderRadius: 0,
              boxShadow: 'none',
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  css={{
                    fontWeight: 'bold',
                    fontSize: theme.spacing(2),
                  }}
                >
                  {content.get('NAME')}
                </TableCell>
                <TableCell
                  css={{
                    fontWeight: 'bold',
                    fontSize: theme.spacing(2),
                  }}
                  align="left"
                >
                  {content.get('DATE_OF_BIRTH')}
                </TableCell>
                <TableCell
                  css={{
                    fontWeight: 'bold',
                    fontSize: theme.spacing(2),
                  }}
                  align="left"
                >
                  {content.get('PHONE')}
                </TableCell>
                <TableCell
                  css={{
                    fontWeight: 'bold',
                    fontSize: theme.spacing(2),
                  }}
                  align="left"
                >
                  {content.get('PERMISSIONS')}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {records?.map((item: any) => (
                <TableRow
                  key={''}
                  css={{
                    borderRadius: 0,
                    boxShadow: 'none',
                  }}
                >
                  <TableCell
                    align="left"
                    css={{
                      verticalAlign: 'top',
                      fontSize: theme.spacing(2),
                      color: colors.tableBodyData,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '10px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Avatar>{`${item?.firstName?.charAt(
                          0,
                        )}${item?.lastName?.charAt(0)}`}</Avatar>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <div>{`${item?.firstName} ${item?.lastName}`}</div>
                        <div>{item?.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell
                    align="left"
                    css={{
                      verticalAlign: 'top',
                      fontSize: theme.spacing(2),
                      color: colors.tableBodyData,
                    }}
                  >
                    {item?.birthDate}
                  </TableCell>
                  <TableCell
                    align="left"
                    css={{
                      verticalAlign: 'top',
                      fontSize: theme.spacing(2),
                      color: colors.tableBodyData,
                    }}
                  >
                    {item?.phoneNumber}
                  </TableCell>
                  <TableCell
                    align="left"
                    css={{
                      verticalAlign: 'top',
                      fontSize: theme.spacing(2),
                      color: colors.tableBodyData,
                    }}
                  >
                    {item?.role}
                  </TableCell>
                  <TableCell
                    align="left"
                    css={{
                      verticalAlign: 'top',
                      fontSize: theme.spacing(2),
                      color: colors.tableBodyData,
                    }}
                  >
                    <Menu
                      items={
                        item?.role === 'ADMIN' ? AdminUserMenu : EditorUserMenu
                      }
                      value={item}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Grid>
          <Pagination {...paginationProps} />
        </Grid>
      </Fragment>
    </Fragment>
  );
};
